import React, { useState, useEffect, useContext, useRef, useMemo } from "react";
import cx from "classnames";
import { Nav, NavItem, NavLink } from "reactstrap";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";

import global from "../../../scss/dhp.scss";

import AssetName from "./Common/AssetName";
import { Input } from "../../ui/input";
import { Icon } from "../../ui/icon";
import ColorBucket from "../ContextualMenu/ColorBucket";
import { changeBucketColorAccordingBackground, hexToRGB } from "../../../_helpers/utils";
import useAlignment from "../../../hooks/useAlignment";
import { COUNTDOWN } from "../../../constants/editor";
import { widgetConfig } from "../editor_config";
import useAddWidget from "../../../hooks/useAddWidget";
import useElementInnerHtml from "../../../hooks/useElementInnerHtml";
import useCountdown from "../../../hooks/useCountdown";
import { EditorContext } from "../../../containers/editor/EditorLayout";

let style = Object.assign({}, global);

const generateTimeOptions = () => {
  const times = [];

  for (let h = 1; h <= 12; h++) {
    for (let m = 0; m < 60; m += 15) {
      const hh = String(h).padStart(2, "0");
      const mm = String(m).padStart(2, "0");

      times.push({ name: `${hh}:${mm}` });
    }
  }

  return times;
};

const timeOptions = generateTimeOptions();
const ampmOptions = [{ name: "AM" }, { name: "PM" }];

// Countdown Preview Component (lightweight)
const CountdownPreview = ({ config, updateConfig }) => {
  const { d, h, m, s } = useCountdown(config);

  const blocks =
    d > 0
      ? [
          { label: "DAYS", value: d },
          { label: "HOURS", value: h },
          { label: "MINUTES", value: m },
        ]
      : [
          { label: "HOURS", value: h },
          { label: "MINUTES", value: m },
          { label: "SECONDS", value: s },
        ];

  return (
    <div className={style["mb-2"]}>
      <label className={cx(style["custom-control-label"], style["fw-6"], style["mb-1"], style["font-15"])}>Style</label>

      {/* Countdown style 1 */}
      <div
        // style={{ display: "flex", gap: 8, marginTop: 10 }}
        className={cx(style["countdown-item-wrap"], {
          [style["active"]]: config.style === "style 1",
        })}
        onClick={() => updateConfig("style", "style 1")}>
        {blocks.map((b, i) => (
          <div
            className={style["style-item"]}
            key={i}
            style={{
              background: config.backgroundColor,
              color:
                changeBucketColorAccordingBackground(hexToRGB(config.backgroundColor)) === "dark"
                  ? "#ffffff"
                  : "#000000",
              fontFamily: config.fontFamily,
            }}>
            <div style={{ fontWeight: "bold" }}>{String(b.value).padStart(2, "0")}</div>
            <div style={{ fontSize: 10 }}>{b.label}</div>
          </div>
        ))}
      </div>

      {/* Countdown style 2 */}
      <div
        // style={{ display: "flex", gap: 8, marginTop: 10 }}
        className={cx(style["countdown-item-wrap"], style["mt-2"], {
          [style["active"]]: config.style === "style 2",
        })}
        onClick={() => updateConfig("style", "style 2")}>
        {blocks.map((b, i) => (
          <div
            className={style["style-item"]}
            key={i}
            style={{
              background: config.backgroundColor,
              color:
                changeBucketColorAccordingBackground(hexToRGB(config.backgroundColor)) === "dark"
                  ? "#ffffff"
                  : "#000000",
              fontFamily: config.fontFamily,
            }}>
            {/* LABEL (TOP) */}
            <div style={{ fontSize: 10 }}>{b.label}</div>

            {/* SEPARATOR LINE */}
            <div
              style={{
                width: "100%",
                height: 1,
                opacity: 0.6,
                backgroundColor:
                  changeBucketColorAccordingBackground(hexToRGB(config.backgroundColor)) === "dark"
                    ? "#ffffff"
                    : "#000000",
                marginBottom: 6,
              }}
            />

            {/* VALUE (BOTTOM) */}
            <div
              style={{
                fontWeight: "bold",
              }}>
              {String(b.value).padStart(2, "0")}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// DateTime Tab
const DateTime = ({ config, updateConfig, dateFormat, setCountDownConfig }) => {
  const toISODate = (dateStr, format) => {
    if (!dateStr) return "";

    const parts = dateStr.split("/");

    let day, month, year;

    if (format === "DD/MM/YYYY") {
      [day, month, year] = parts;
    } else if (format === "MM/DD/YYYY") {
      [month, day, year] = parts;
    } else {
      return dateStr;
    }

    return `${year}-${month}-${day}`;
  };

  const fromISODate = (iso, format) => {
    if (!iso) return "";

    const [year, month, day] = iso.split("-");

    if (format === "DD/MM/YYYY") {
      return `${day}/${month}/${year}`;
    } else if (format === "MM/DD/YYYY") {
      return `${month}/${day}/${year}`;
    }

    return iso;
  };

  const handleDateChange = (value, dateFormat, setCountDownConfig) => {
    const isoDate = toISODate(value, dateFormat);

    setCountDownConfig(prev => {
      if (prev.endDate === isoDate) return prev; // prevent re-render

      return {
        ...prev,
        endDate: isoDate,
      };
    });
  };

  return (
    <div>
      {/* Calender date field */}
      <label className={cx(style["custom-control-label"], style["fw-6"], style["mb-1"], style["font-15"])}>
        Ends On
      </label>
      <div className={cx(style["custom-date-picker"], style["required-mark"], style["mb-3"])}>
        <Input
          // type="date_calendar"
          type="countdown_calendar"
          id="countdown_calender"
          inputName={"Ends On"}
          dateFormat={dateFormat}
          value={fromISODate(config.endDate, dateFormat)} // controlled
          updateState={e => {
            const val = e?.target?.value || e;
            handleDateChange(val, dateFormat, setCountDownConfig);
          }}
        />
      </div>

      {/* TIME DROPDOWN */}
      <div className={cx(style["mb-3"], style["time-wrap"])}>
        <label className={cx(style["custom-control-label"], style["fw-6"], style["mb-1"], style["font-15"])}>
          Time
        </label>

        <div className={cx(style["d-flex"], style["align-items-center"], style["gap-8"])}>
          <div className={style["time-drop"]} style={{ flex: 1 }}>
            <Input
              type="dropdown"
              name="time"
              classNamePrefix="select"
              defaultValue={{ name: config.endTime || "12:00" }}
              value={timeOptions.find(t => t.name === config.endTime)}
              getOptionValue={option => option.name}
              getOptionLabel={option => option.name}
              options={timeOptions}
              updateState={option => updateConfig("endTime", option.name)}
              noOptionsMessage={() => "No results found"}
            />
          </div>

          {/* AM / PM DROPDOWN */}
          <div className={cx(style["apm-drop"])}>
            <Input
              type="dropdown"
              name="ampm"
              classNamePrefix="select"
              defaultValue={{ name: config.ampm || "AM" }}
              value={ampmOptions.find(t => t.name === config.ampm)}
              getOptionValue={option => option.name}
              getOptionLabel={option => option.name}
              options={ampmOptions}
              updateState={option => updateConfig("ampm", option.name)}
              noOptionsMessage={() => "No results found"}
            />
          </div>
        </div>
      </div>

      {/* Timezone */}
      <div className={cx(style["mb-3"])}>
        <label className={cx(style["custom-control-label"], style["fw-6"], style["mb-1"], style["font-15"])}>
          Timezone
        </label>
        <div>
          <Input type="text" inputName={"Timezone"} value={config.timezone} readOnly />
        </div>
      </div>
    </div>
  );
};

// Settings Tab
const CountdownSettings = ({ config, updateConfig }) => {
  let fonts = JSON.parse(localStorage?.getItem("allFonts"));
  let availableFonts = fonts.filter(font => font.bold_weight !== "");

  const [backgroundEyeDropperColor, setBackgroundEyeDropperColor] = useState();
  const [loadCureentFormatting, setLoadCureentFormatting] = useState();
  const [updateContext, setUpdateContext] = useState(false);

  // derived, not stored
  const backgroundColor = config.backgroundColor;
  const backgroundBucketColor = useMemo(
    () => (backgroundColor ? changeBucketColorAccordingBackground(hexToRGB(backgroundColor)) : undefined),
    [backgroundColor]
  );

  const setBackgroundColor = color => updateConfig("backgroundColor", color);

  // update Background color using eye dropper
  useEffect(() => {
    if (!backgroundEyeDropperColor) return;

    setUpdateContext(true);
    setLoadCureentFormatting(false);
    updateConfig("backgroundColor", backgroundEyeDropperColor);
  }, [backgroundEyeDropperColor]);

  return (
    <div>
      {/* Time Format */}
      <label className={cx(style["custom-control-label"], style["fw-6"], style["font-15"], style["mb-1"])}>
        Time Format
        <span className={cx(style["custom-tooltip"], style["ml-2"], style["info-tooltip"])}>
          <Icon icon="information" additionalclass="font-base" />
          <span className={cx(style["custom-tooltip-content"], style["bottom"])}>
            Automatically switches format based on time remaining - shows seconds when under 24 hours.
          </span>
        </span>
      </label>
      {/* <div className={cx(style["info-link-content"], style["rounded"], style["shadow-sm"])}>
        Automatically switches format based on time remaining - shows seconds when under 24 hours.
      </div> */}
      <div className={cx(style["mb-3"])}>
        <Input type="text" inputName={"Timeformat"} value="Auto" readOnly />
      </div>

      {/* Font Section */}
      <div className={cx(style["toolset-item"], style["family-wrap"], style["font-common-action"])}>
        <div className={cx(style["slidelabel"], style["fw-6"], style["mb-1"], style["font-15"])}> Font </div>
        <div>
          <Input
            type="dropdown"
            name="family"
            classNamePrefix="select"
            defaultValue={{ name: config.fontFamily || "Open Sans" }}
            getOptionValue={option => option.name}
            getOptionLabel={option => <span style={{ fontFamily: option.name }}>{option.name}</span>}
            options={availableFonts}
            updateState={option => updateConfig("fontFamily", option.name)}
            noOptionsMessage={() => "No results found"}
          />
        </div>
      </div>

      {/* Countdown background color */}
      <div className={cx(style["line-controls-wrap"], style["mb-2"])}>
        <div className={style["slidelabel"]}>Background Color </div>
        <ColorBucket
          color={backgroundColor}
          setColor={setBackgroundColor}
          iconBucketColor={backgroundBucketColor}
          position={"toolbar"}
          eyedropperColor={backgroundEyeDropperColor}
          setEyedropperColor={setBackgroundEyeDropperColor}
          setLoadCurrentFormatting={setLoadCureentFormatting}
          setUpdateContext={setUpdateContext}
          moveDropdownUp={250}
        />
      </div>
    </div>
  );
};

// Main Component
const Countdown = props => {
  let { metadata, widgets, updateWidgets } = useContext(EditorContext);

  const dateFormat = (useSelector(state => state?.auth?.user?.date_format) || "MM-DD-YYYY").replaceAll("-", "/");
  const userInfo = useSelector(state => state?.auth?.user);

  const [activeTab, setActiveTab] = useState(0);
  const [addWidgetClicked, setAddWidgetClicked] = useState(false);
  const [assetInnerContent, setAssetInnerContent] = useState();
  const [countDownConfig, setCountDownConfig] = useState({
    endDate: "",
    endTime: "12:00",
    ampm: "AM",
    timezone: userInfo.time_zone,
    backgroundColor: "#000000",
    fontFamily: "Open Sans",
    style: "style 1",
  });

  const originalConfigRef = useRef(null);

  const { postion: getPosition } = useAlignment(
    addWidgetClicked,
    widgetConfig[COUNTDOWN]?.width,
    widgetConfig[COUNTDOWN]?.height,
    "middle-center"
  );
  const getNewWidgetObject = useAddWidget(
    addWidgetClicked,
    COUNTDOWN,
    widgetConfig[COUNTDOWN].dataAttr,
    getPosition,
    widgetConfig[COUNTDOWN]?.width,
    widgetConfig[COUNTDOWN]?.height,
    false,
    countDownConfig
  );
  useElementInnerHtml(
    addWidgetClicked,
    setAddWidgetClicked,
    COUNTDOWN,
    assetInnerContent,
    widgetConfig[COUNTDOWN].dataAttr,
    getNewWidgetObject
  );

  const isChanged = JSON.stringify(countDownConfig) !== originalConfigRef.current;

  // Update helper
  const updateConfig = (key, value) => {
    setCountDownConfig(prev => {
      if (!value) return prev; //block empty overwrite

      return {
        ...prev,
        [key]: value,
      };
    });
  };

  // Insert into editor (you already have handler)
  const handleAdd = () => {
    setAssetInnerContent("");
    setAddWidgetClicked(true);
  };

  const getChangedFields = (oldConfig, newConfig) => {
    const changed = {};

    Object.keys(newConfig).forEach(key => {
      if (newConfig[key] !== oldConfig[key]) {
        changed[key] = newConfig[key];
      }
    });

    return changed;
  };

  // update exsisting caountdown
  const handleUpdate = () => {
    const activeId = metadata.activeWidgetId?.[0];
    const targetWidgetIndex = widgets.findIndex(w => w.id === activeId);

    if (targetWidgetIndex === -1) return;

    const currentWidget = widgets[targetWidgetIndex];

    // get only changed values
    const changedFields = getChangedFields(currentWidget.config, countDownConfig);

    const newArray = Object.assign([...widgets], {
      [targetWidgetIndex]: {
        ...currentWidget,
        config: {
          ...currentWidget.config, // keep everything
          ...changedFields, // only override changed
        },
      },
    });

    updateWidgets(newArray);

    // reset baseline
    originalConfigRef.current = JSON.stringify({
      ...currentWidget.config,
      ...changedFields,
    });
  };

  useEffect(() => {
    if (metadata.activeWidgetId.length === 1 && metadata.activeWidgetType[0] === COUNTDOWN) {
      const activeId = metadata.activeWidgetId?.[0];
      const activeWidget = widgets.find(w => w.id === activeId);

      if (activeWidget?.data["data-asset-type"] === COUNTDOWN) {
        const configFromWidget = activeWidget.config;
        setCountDownConfig(configFromWidget);
        originalConfigRef.current = JSON.stringify(configFromWidget); // STORE ORIGINAL (for compare)
      }
    } else {
      const defaultConfig = {
        endDate: "",
        endTime: "12:00",
        ampm: "AM",
        timezone: userInfo.time_zone,
        backgroundColor: "#000000",
        fontFamily: "Open Sans",
        style: "style 1",
      };
      setCountDownConfig(defaultConfig);
      originalConfigRef.current = JSON.stringify(defaultConfig);
    }
  }, [metadata.activeWidgetId, widgets]);

  return (
    <div className={cx(style["editor-asset-inner"], style["countdown-asset"])}>
      <AssetName handleWidgetAction={props.handleWidgetAction} assetName={props.assetName} />
      <Nav tabs className={cx(style["line-tabs"], style["mr-20"])}>
        <NavItem className={style["mb-0"]}>
          <NavLink className={activeTab === 0 ? "active" : ""} onClick={() => setActiveTab(0)}>
            Date/Time
          </NavLink>
        </NavItem>

        <NavItem className={style["mb-0"]}>
          <NavLink className={activeTab === 1 ? "active" : ""} onClick={() => setActiveTab(1)}>
            Settings
          </NavLink>
        </NavItem>
      </Nav>

      <div className={cx(style["customScroll"], style["scroll-Y"], style["assets-wrapper"])}>
        {/* Tabs */}
        {activeTab === 0 && (
          <DateTime
            config={countDownConfig}
            updateConfig={updateConfig}
            dateFormat={dateFormat}
            setCountDownConfig={setCountDownConfig}
          />
        )}

        {activeTab === 1 && <CountdownSettings config={countDownConfig} updateConfig={updateConfig} />}

        {/* LIVE PREVIEW */}
        <CountdownPreview config={countDownConfig} updateConfig={updateConfig} />
      </div>

      {/* Button */}
      {countDownConfig.endDate && (
        <div className={cx(style["fixed-bottom"], style["text-center"], style["mt-auto"])}>
          {metadata.activeWidgetId.length === 1 &&
          metadata.activeWidgetId[0] &&
          metadata.activeWidgetType[0] === COUNTDOWN ? (
            <button
              className={cx(
                style["btn"],
                style["w-100"],
                {
                  [style["btn-primary"]]: isChanged,
                },
                {
                  [style["btn-secondary"]]: !isChanged,
                },
                {
                  [style["disable"]]: !isChanged,
                }
              )}
              onClick={handleUpdate}>
              Update Countdown
            </button>
          ) : (
            <button className="btn btn-primary w-100" onClick={handleAdd}>
              Add Countdown
            </button>
          )}
        </div>
      )}
    </div>
  );
};

Countdown.propTypes = {
  handleWidgetAction: PropTypes.func.isRequired,
  assetName: PropTypes.string.isRequired,
  assetType: PropTypes.string.isRequired,
};

export default Countdown;
